import { Component, type ErrorInfo, type ReactNode } from 'react'

// A page that throws while rendering used to take the whole app with it: a blank window, no words, nothing to click
// (Don, 2026-10-07, mid-draft). The boundary keeps the shell standing, says what broke in one line, and offers the
// two things a person can do about it. The key on the mount resets it when the route changes.

interface Props { children: ReactNode; where?: string }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State { return { error } }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // the console is the only log a browser has; the line is enough to find it in a screenshot
    console.error(`[scout] ${this.props.where ?? 'the page'} could not be drawn:`, error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    const message = this.state.error.message || String(this.state.error)
    return (
      <section className="page" role="alert">
        <h1>Something went wrong on this screen</h1>
        <p className="lede">Nothing was lost: what you wrote and what Scout answered are on file. The screen itself could not be drawn.</p>
        <p className="error small"><code>{message.slice(0, 300)}</code></p>
        <p>
          <button type="button" className="button primary" onClick={() => this.setState({ error: null })}>Try again</button>{' '}
          <button type="button" className="button" onClick={() => window.location.reload()}>Reload the app</button>
        </p>
      </section>
    )
  }
}
